import React, { useEffect, useState } from 'react'
import { Container, ImageList, ImageListItem, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import { getPublicUrlFromFileKey } from '../../services/API/Api';
import { InstaVideoIcon } from '../../Icons/svg/instaVideo';

const PlaceRelatedPost = ({ placeId, type, onPostClick }: { placeId: string, type: string, onPostClick?: (postId: string) => void }) => {
    const navigate = useNavigate();
    const [postsData, setPostsData] = useState<any[]>([]);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const res = await request<{ success: boolean, data: { posts: any[] } }>(
                    `/places/${placeId}/posts`,
                    {
                        authorization: true,
                        method: 'GET', // explicit for clarity
                    },
                );

                // Resolve view URLs for videos that only have fileKey
                const processedPosts = await Promise.all(
                    res.data.posts.map(async (post: any) => {
                        if (!post?.files?.length) return post;
                        const firstFile = post.files[0];

                        // If fileURL is missing but we have a fileKey (likely a video), fetch the public URL
                        if (!firstFile.fileURL && firstFile.fileKey) {
                            try {
                                const viewRes = await getPublicUrlFromFileKey(firstFile.fileKey, 'post');
                                firstFile.fileURL = viewRes?.data?.files?.[0]?.viewUrl || '';
                            } catch (error) {
                                console.error('Failed to fetch viewUrl for video', error);
                            }
                        }
                        return post;
                    }),
                );

                // Filter posts by required type
                const filtered = processedPosts.filter((post: any) => {
                    const fileType = post?.files?.[0]?.fileType || '';
                    const isVideo = fileType.startsWith('video');
                    if (type === 'shorts') return isVideo;
                    if (type === 'posts') return !isVideo;
                    return true; // fallback – show everything
                });

                setPostsData(filtered);
            } catch (error) {
                console.error('Error fetching place related posts', error);
            }
        };

        fetchPosts();
    }, [placeId, type]);


    return (
        <Container maxWidth="sm" sx={{ flex: 1, }}>
            <Typography variant="h6" color="text.primary">
                {postsData.length}
                <ImageList
                    sx={{
                        width: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflowY: 'hidden',
                        overflowX: 'hidden',
                    }}
                    cols={3}
                    rowHeight={164} // unified cell height
                >
                    {postsData.map((item, index) => {
                        const file = item?.files?.[0] || {};
                        const isVideo = (file.fileType || '').includes('video');

                        return (
                            <ImageListItem
                                key={file.fileURL || file.fileKey}
                                sx={{ position: 'relative' }}
                                onClick={() => {
                                    if (onPostClick) {
                                        onPostClick(item._id);
                                    } else {
                                        navigate('/profile/post-detail', {
                                            state: {
                                                posts: postsData,
                                                selectedIndex: index,
                                                placeId: placeId,
                                                type: type
                                            }
                                        });
                                    }
                                }}
                            >
                                {isVideo ? (
                                    <video
                                        src={file.fileURL}
                                        muted
                                        playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <img
                                        srcSet={`${file.fileURL}`}
                                        src={`${file.fileURL}`}
                                        alt={file.fileURL}
                                        loading="lazy"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                )}

                                {isVideo && (
                                    <Box sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.4)' }}>
                                        <InstaVideoIcon />
                                    </Box>
                                )}
                            </ImageListItem>
                        );
                    })}
                </ImageList>
            </Typography>
        </Container>
    )
}

export default PlaceRelatedPost